import { Select, SelectContent, SelectItem, SelectTrigger, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorName, LandscapeType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { GeomodelServerLabelFiltersOperation } from '@/api/rest/resources/types/organization';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { LandCoverChangeChart } from '@/components/Charts/LandCoverChange';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { RadiusType } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { RADIUS_OPTIONS } from '@/components/MapOverview/components/ReportingBoundsLayer';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';

type LandcoverLanduseGraphTileType = Partial<GraphTileProps & { selectedBenchmark?: string | null }>;
type LandcoverLanduseRadius = RadiusType | typeof DEFAULT_RADIUS;

const LANDCOVER_LANDUSE_AVAILABLE_YEARS = 11; // 2016 - 2026 (inclusive)
export const DEFAULT_RADIUS = '0';
const UNIT = 'global.ui.unit.km';

export const LandcoverLanduseGraphTile = (props: LandcoverLanduseGraphTileType) => {
  const { t } = useTranslation();
  const [radius, setRadius] = useState<LandcoverLanduseRadius>(DEFAULT_RADIUS);

  return (
    <Stack center data-testid='land-cover-graph-tile'>
      <Stack direction='row' spacing={2} center centerMain className='w-full justify-end mt-2'>
        <Stack direction='row' spacing={2} center centerMain>
          <span>{t('shared.projects.map.labels.radius')}</span>
          <Select value={radius} onValueChange={(value) => setRadius(value as LandcoverLanduseRadius)}>
            <SelectTrigger>{`${radius} ${t(UNIT)}`}</SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_RADIUS}>{`${DEFAULT_RADIUS} ${t(UNIT)}`}</SelectItem>
              {RADIUS_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {`${option} ${t(UNIT)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
      </Stack>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <GraphContainer radius={radius} {...props} />
      </Stack>
    </Stack>
  );
};

const GraphContainer = ({
  selectedBenchmark,
  radius,
  plotGroupId,
}: LandcoverLanduseGraphTileType & { radius: LandcoverLanduseRadius }) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const indicator = GraphTileType.landcover_landuse;

  const organizationId = useOrganizationId();
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);

  const { [IndicatorName.landcover_by_landscape_type_historic_pct_graph]: lulcFact } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      IndicatorName.landcover_by_landscape_type_historic_pct_graph,
      null,
      {
        landscape_radius_km: radius,
      },
      plotGroupId,
      {
        label_filters_operation: GeomodelServerLabelFiltersOperation.contains,
        limit: Object.keys(LandscapeType).length * LANDCOVER_LANDUSE_AVAILABLE_YEARS,
      },
    );

  const noChartData = !lulcFact || !Array.isArray(lulcFact.value) || lulcFact.value.length === 0;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  return (
    <LandCoverChangeChart
      data={lulcFact as PlotReportFactElement<GraphFact[]>}
      customBenchmark={activeCustomBenchmark}
      data-testid='land-cover-change-chart'
    />
  );
};
